import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { UserCheck, MapPin, DollarSign } from "lucide-react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { mediaUrl } from "@/lib/api";
import { budgetText, jobLocationText } from "@/lib/jobDisplay";
import { from } from "@/lib/cameFrom";
import { formatDate } from "@/lib/dates";

/**
 * The requests a client sent to this FixyBee **by name**, with an address.
 *
 * Until now they had none. A directed request announced itself with one push
 * and one bell notification, and a notification is read once: after that the
 * only trace was a badge on `/provider/browse`, sorted to the top of a board
 * whose entire premise is *other people's* demand. Nobody goes looking inside
 * "explore" for what was asked of them personally. The badge stays where it is
 * — it is the right reminder on that page — and this is the place it reminds
 * you of.
 *
 * `GET /jobs?directed=true`, not a new endpoint: the board already carries the
 * fence that keeps a directed job private (`directed_provider_id IS NULL OR =
 * me`), so reading it from the other side is the whole difference. The rows
 * come back with the requester, the trade and this reader's own quote status
 * resolved server-side, because a card that has to say who is asking cannot
 * afford a round trip per row.
 *
 * Every status, not only open: a request that was accepted or closed is still
 * part of what this FixyBee was asked for. There is no expired state to render
 * — a directed job has no deadline (Jorge, 2026-09-07), and the requester's
 * "open to all" clears the column, which drops the row out of this list rather
 * than giving it a state inside it.
 */
export default function DirectedRequests() {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setFailed(false);
    api.get("/jobs?directed=true&limit=50")
      .then((r) => { if (live) setJobs(r.data.jobs || []); })
      .catch(() => { if (live) setFailed(true); })
      // The same `finally` the sibling lists learned to use (DY2.2 V4): a
      // rejected request used to leave a page animating skeletons forever.
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [reload]);

  useEffect(() => {
    // Opening this page is what makes the sidebar count stale — the FixyBee is
    // about to answer one of them. The layout listens for this.
    window.dispatchEvent(new Event("fixy:directed-changed"));
  }, [jobs]);

  /**
   * What the card says about where this request stands.
   *
   * The job's own status wins whenever it is not open: once a quote was
   * accepted, "you have not answered" is false whoever sent it. Below that it
   * is about this reader's quote and nobody else's — withdrawn is shown as
   * itself and counted as unanswered, because the server lets them send
   * another one.
   */
  const stateOf = (j) => {
    if (j.status !== "open") {
      return {
        key: j.status,
        label: t(`directedRequests.jobStatus.${j.status}`, j.status),
        cls: j.status === "completed" ? "bg-success text-cream"
          : j.status === "in_progress" ? "bg-info text-cream" : "bg-sand",
      };
    }
    if (j.my_quote_status === "sent" || j.my_quote_status === "accepted") {
      return { key: "quoted", label: t("directedRequests.state.quoted"), cls: "bg-success text-cream" };
    }
    if (j.my_quote_status === "declined") {
      return { key: "declined", label: t("directedRequests.state.declined"), cls: "bg-sand" };
    }
    if (j.my_quote_status === "stale") {
      // T19 X2. The requester edited the request after this FixyBee answered;
      // the answer is outdated and a new one is free.
      return { key: "stale", label: t("jobDetail.quoteStatus.stale"), cls: "bg-info text-cream" };
    }
    if (j.my_quote_status === "withdrawn") {
      return { key: "withdrawn", label: t("directedRequests.state.withdrawn"), cls: "bg-sand" };
    }
    return { key: "pending", label: t("directedRequests.state.pending"), cls: "bg-hive text-worker" };
  };

  return (
    <DashboardLayout role="provider">
      <div data-testid="directed-requests-page">
        <h1 className="font-display text-3xl md:text-4xl font-bold text-worker">
          {t("directedRequests.title")}
        </h1>
        <p className="mt-1 text-smoke">{t("directedRequests.subtitle")}</p>

        <div className="mt-6">
          {loading ? (
            <div className="space-y-3">{Array(3).fill(0).map((_, i) => <div key={i} className="skeleton h-32" />)}</div>
          ) : failed ? (
            <div className="card-base p-10 text-center" data-testid="directed-requests-error">
              <UserCheck size={36} className="mx-auto text-smoke" />
              <p className="mt-3 text-smoke">{t("common.loadFailed")}</p>
              <button className="fx-btn fx-btn--primary mt-4" onClick={() => setReload((n) => n + 1)}
                      data-testid="directed-requests-retry">{t("common.retry")}</button>
            </div>
          ) : jobs.length === 0 ? (
            <div className="card-base p-10 text-center" data-testid="directed-requests-empty">
              <UserCheck size={36} className="mx-auto text-smoke" />
              <h3 className="mt-4 font-display text-xl font-bold text-worker">
                {t("directedRequests.emptyTitle")}
              </h3>
              <p className="text-smoke mt-1">{t("directedRequests.emptyBody")}</p>
            </div>
          ) : (
            <div className="grid grid-cols-[minmax(0,1fr)] gap-3" data-testid="directed-requests-list">
              {/* `minmax(0,1fr)` rather than a bare `grid` — see ClientJobs
                  for the whole of it (T13-M2). Same card, same `truncate`
                  title, same 462px floor on a 360px phone. */}
              {jobs.map((j) => {
                const st = stateOf(j);
                return (
                  <Link
                    key={j.job_id}
                    to={`/provider/jobs/${j.job_id}`}
                    state={from("/provider/requests-for-you")}
                    className="card-base p-5 block"
                    data-testid={`directed-job-${j.job_id}`}
                  >
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0 flex-1">
                        <h3 className="font-display text-lg font-bold text-worker truncate">{j.title}</h3>
                        {j.category_name && (
                          <p className="text-xs text-smoke font-mono mt-0.5">{j.category_name}</p>
                        )}
                        <p className="text-sm text-smoke line-clamp-2 mt-1">{j.description}</p>
                      </div>
                      <span className={`badge whitespace-nowrap ${st.cls}`}
                            data-testid={`directed-job-state-${j.job_id}`}>{st.label}</span>
                    </div>

                    {/* Who is asking — display name and city, the same two
                        fields the job page shows and for the same reason: the
                        coordination channel is the in-app chat, which opens on
                        an accepted quote and not on a request being read. */}
                    {j.client_snippet?.full_name && (
                      <div className="mt-3 flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-hive flex items-center justify-center font-display text-xs font-bold text-worker shrink-0 overflow-hidden">
                          {j.client_snippet.avatar_url
                            ? <img src={mediaUrl(j.client_snippet.avatar_url)} alt="" className="w-full h-full object-cover" />
                            : j.client_snippet.full_name.charAt(0).toUpperCase()}
                        </div>
                        <span className="text-sm text-worker truncate"
                              data-testid={`directed-job-requester-${j.job_id}`}>
                          {t("directedRequests.requestedBy", { name: j.client_snippet.full_name })}
                        </span>
                      </div>
                    )}

                    <div className="mt-3 flex gap-3 text-xs text-smoke font-mono flex-wrap">
                      <span className="flex items-center gap-1"><DollarSign size={12} />{budgetText(t, j.budget_min, j.budget_max)}</span>
                      {jobLocationText(j) && (
                        <span className="flex items-center gap-1"><MapPin size={12} />{jobLocationText(j)}</span>
                      )}
                      <span>{formatDate(j.created_at)}</span>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
